import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { incidentService, Incident, CreateIncidentData } from '../../api/incidents';
import { tripService } from '../../api/trips';
import { Vehicle } from '../../api/vehicles';
import { getApiErrorMessage } from '../../api';
import {
  AlertTriangle,
  Search,
  RefreshCw,
  Plus,
  Car,
  MapPin,
  CalendarDays,
  Clock,
  AlignLeft,
  Image as ImageIcon,
  Eye,
  Edit3,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  User as UserIcon,
  Info,
  Loader2,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ConfirmModal,
  showToast,
} from '../../components/ui/SharedLayout';

const formatDate = (value?: string | Date | null) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const ITEMS_PER_PAGE = 20;

const IncidentesPage = () => {
  const { user, setPageTitle } = useAuth();
  const [incidentes, setIncidentes] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [availableVehicles, setAvailableVehicles] = useState<Vehicle[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIncidente, setEditingIncidente] = useState<Incident | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<CreateIncidentData> & { involved_employees?: number[] }>({
    vehicle_id: undefined,
    date: '',
    time: '',
    description: '',
    location: '',
    involved_employees: [],
  });
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [viewingIncident, setViewingIncident] = useState<Incident | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setPageTitle('Meus Incidentes');
  }, [setPageTitle]);

  const fetchIncidentes = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!user?.employee_id) {
      setIncidentes([]);
      setAvailableVehicles([]);
      setError('Seu usuário não está vinculado a um funcionário.');
      setLoading(false);
      return;
    }

    try {
      const [incidentsRes, tripsRes] = await Promise.all([
        incidentService.getAll(),
        tripService.getAll(),
      ]);

      const userIncidents = incidentsRes.filter(inc =>
        inc.involved_employees?.includes(user.employee_id!)
      );
      const userVehicles = Array.from(
        new Map(
          tripsRes
            .filter((trip) => trip.employee_id === user.employee_id && trip.vehicle)
            .map((trip) => [trip.vehicle!.id, trip.vehicle!]),
        ).values(),
      );

      setIncidentes(userIncidents);
      setAvailableVehicles(userVehicles);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao carregar dados'));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchIncidentes();

    return () => {
      photoPreviews.forEach(url => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchIncidentes]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'select-multiple') {
      const select = e.target as HTMLSelectElement;
      const selectedOptions = Array.from(select.selectedOptions).map(option => Number(option.value));
      setFormData(prev => ({ ...prev, [name]: selectedOptions }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setPhotoFiles(prev => [...prev, ...files]);
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPhotoPreviews(prev => [...prev, ...newPreviews]);
  };

  const handleRemovePhoto = (idx: number) => {
    URL.revokeObjectURL(photoPreviews[idx]);
    setPhotoFiles(prev => prev.filter((_, i) => i !== idx));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleCreate = () => {
    setEditingIncidente(null);
    setError(null);
    setFormData({
      vehicle_id: undefined,
      date: '',
      time: '',
      description: '',
      location: '',
      involved_employees: user?.employee_id ? [user.employee_id] : [],
    });
    setPhotoFiles([]);
    setPhotoPreviews([]);
    setIsModalOpen(true);
  };

  const handleEdit = (incidente: Incident) => {
    setEditingIncidente(incidente);
    setError(null);
    setFormData({
      vehicle_id: incidente.vehicle_id,
      date: incidente.date || '',
      time: incidente.time || '',
      description: incidente.description || '',
      location: incidente.location || '',
      involved_employees: incidente.involved_employees || [],
    });
    setPhotoFiles([]);
    setPhotoPreviews([]);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deletingId == null) return;
    const targetId = deletingId;
    try {
      await incidentService.delete(targetId);
      await fetchIncidentes();
      showToast({
        type: 'success',
        title: 'Incidente excluído com sucesso',
      });
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao excluir incidente'));
      showToast({
        type: 'error',
        title: 'Não foi possível excluir o incidente',
        description: getApiErrorMessage(err, 'Tente novamente em alguns segundos.'),
      });
    } finally {
      setDeletingId(null);
    }
  };

  const confirmAndDelete = (id: number) => {
    setDeletingId(id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const imagesBase64 = await Promise.all(photoFiles.map(file => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }));
      const payload = {
        vehicle_id: Number(formData.vehicle_id),
        date: formData.date || '',
        time: formData.time || '',
        description: formData.description || '',
        location: formData.location || '',
        involved_employees: Array.from(
          new Set([
            ...(editingIncidente?.involved_employees || formData.involved_employees || []),
            ...(user?.employee_id ? [user.employee_id] : []),
          ]),
        ),
        ...(imagesBase64.length > 0 ? { images: imagesBase64 } : {}),
      };
      if (editingIncidente) {
        await incidentService.update(editingIncidente.id, payload);
      } else {
        await incidentService.create(payload);
      }
      setIsModalOpen(false);
      setEditingIncidente(null);
      setFormData({
        vehicle_id: undefined,
        date: '',
        time: '',
        description: '',
        location: '',
        involved_employees: user?.employee_id ? [user.employee_id] : [],
      });

      photoPreviews.forEach(url => URL.revokeObjectURL(url));
      setPhotoFiles([]);
      setPhotoPreviews([]);
      await fetchIncidentes();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao salvar incidente'));
    } finally {
      setSubmitting(false);
    }
  };

  const filteredIncidentes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return incidentes;
    return incidentes.filter(i =>
      [
        i.id.toString(),
        i.description || '',
        i.location || '',
        i.vehicle?.brand || '',
        i.vehicle?.license_plate || '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [incidentes, searchTerm]);

  const canCreateIncident = useMemo(
    () => availableVehicles.length > 0,
    [availableVehicles.length],
  );

  const totalPages = Math.max(1, Math.ceil(filteredIncidentes.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredIncidentes.length, currentPage, totalPages]);

  const { paginatedIncidentes, pageRange } = useMemo(() => {
    const total = filteredIncidentes.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const sliced = filteredIncidentes.slice(startIndex, endIndex);
    return {
      paginatedIncidentes: sliced,
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredIncidentes, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const renderInvolvedEmployees = (ids: number[] = []) => (
    <div className="flex flex-wrap gap-2">
      {ids.length === 0 ? (
        <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-600">
          Somente você
        </span>
      ) : (
        ids.map((id) => (
          <span
            key={id}
            className={`inline-flex items-center rounded-full px-3 py-1 text-sm gap-1.5 ${
              id === user?.employee_id
                ? 'bg-sky-100 text-sky-800 ring-1 ring-sky-200'
                : 'bg-slate-100 text-slate-700'
            }`}
          >
            {id === user?.employee_id ? (
              <>
                <UserIcon className="h-3 w-3 stroke-[2]" />
                Você
              </>
            ) : (
              `Funcionário #${id}`
            )}
          </span>
        ))
      )}
    </div>
  );

  const resolveVehicle = (item: Incident) => {
    if (item.vehicle?.brand && item.vehicle.license_plate) return item.vehicle;
    const fallback = availableVehicles.find(v => v.id === item.vehicle_id);
    return fallback ?? { brand: `Veículo #${item.vehicle_id}`, license_plate: '-' as string | undefined };
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7 flex flex-col gap-6">
        <PageHeader title="Meus Incidentes" hideTitleInBody />

        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between mb-1">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Operações — Incidentes
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Registre e acompanhe
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                os seus incidentes
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            <Button
              type="button"
              variant="primary"
              onClick={handleCreate}
              disabled={loading || !canCreateIncident}
              className="h-10 px-4"
            >
              <Plus className="h-4 w-4 stroke-[2]" />
              Novo Incidente
            </Button>
            <Button variant="secondary" onClick={() => void fetchIncidentes()} className="h-10 px-4">
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
          </div>
        </div>

        {!canCreateIncident && !loading && !error && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-medium text-amber-800">
            <div className="flex items-start gap-2">
              <Info className="h-[18px] w-[18px] shrink-0 mt-[1px] stroke-[1.9]" />
              <div>
                O cadastro de incidente exige ao menos um veículo acessível no seu escopo (vinculado às suas viagens). Sem essa referência, a criação fica temporariamente bloqueada.
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 text-white shadow-[0_6px_16px_-8px_rgba(239,68,68,0.6)]">
                <AlertTriangle className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                  Incidentes
                </h2>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  {loading
                    ? 'Carregando registros...'
                    : pageRange.total === 0
                      ? 'Nenhum incidente encontrado'
                      : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} incidentes listados (total: ${incidentes.length})`}
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[360px] shrink-0 relative">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400"
                aria-hidden="true"
              />
              <Input
                type="text"
                name="search-incidents"
                placeholder="Pesquisar descrição, local, veículo, placa ou ID..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="!pl-10"
              />
            </div>
          </CardHeader>

          <CardBody className="!py-3">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-slate-600">
                  Carregando incidentes...
                </span>
              </div>
            ) : (
              <Fragment>
                <ModernTable>
                  <Thead>
                    <Tr>
                      <Th>Veículo</Th>
                      <Th>Local</Th>
                      <Th>Data</Th>
                      <Th>Hora</Th>
                      <Th>Descrição</Th>
                      <Th>Fotos</Th>
                      <Th>Criado em</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pageRange.total === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-5 py-14 text-center text-sm text-slate-500"
                        >
                          Nenhum incidente encontrado
                        </td>
                      </tr>
                    ) : (
                      paginatedIncidentes.map((incidente) => {
                        const vehicle = resolveVehicle(incidente);
                        return (
                          <Tr key={incidente.id}>
                            <Td>
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 min-w-[40px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 text-white shadow-[0_6px_16px_-8px_rgba(239,68,68,0.55)]">
                                  <Car className="h-[18px] w-[18px] min-h-[18px] min-w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-semibold text-slate-900" title={vehicle.brand}>
                                    {vehicle.brand}
                                  </div>
                                  <div className="mt-0.5 font-mono text-[12px] tracking-wide text-slate-500 truncate">
                                    {vehicle.license_plate || '-'}
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-start gap-1.5">
                                <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5 stroke-[1.8] text-slate-400" />
                                <span className="block truncate font-medium text-slate-700" title={incidente.location || '-'}>
                                  {incidente.location || '-'}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5">
                                <CalendarDays className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                                <span className="font-medium text-slate-700 tabular-nums text-[12.5px]">
                                  {formatDate(incidente.date)}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5">
                                <Clock className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                                <span className="font-medium text-slate-700 tabular-nums text-[12.5px]">
                                  {incidente.time || '-'}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-start gap-1.5 w-full">
                                <AlignLeft className="h-3.5 w-3.5 shrink-0 mt-0.5 stroke-[1.8] text-slate-400" />
                                <span className="block truncate font-medium text-slate-700 max-w-[320px]" title={incidente.description || '-'}>
                                  {incidente.description || '-'}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              {incidente.images && incidente.images.length > 0 ? (
                                <div className="flex items-center gap-1.5">
                                  <div className="flex -space-x-2">
                                    {incidente.images.slice(0, 3).map((img: { url: string }, idx: number) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={() => setSelectedImage(img.url)}
                                        className="relative h-8 w-8 shrink-0 overflow-hidden rounded-lg border-2 border-white shadow-sm transition-all hover:z-10 hover:scale-110 hover:shadow-md"
                                        title={`Foto ${idx + 1} de ${incidente.images!.length}`}
                                      >
                                        <img src={img.url} alt={`Incidente ${incidente.id}`} className="h-full w-full object-cover" />
                                      </button>
                                    ))}
                                  </div>
                                  {incidente.images.length > 1 && (
                                    <span className="inline-flex items-center rounded-md bg-slate-50 px-2 py-0.5 text-[11.5px] font-bold text-slate-700 ring-1 ring-inset ring-slate-200 tabular-nums">
                                      <ImageIcon className="h-3 w-3 stroke-[1.9] mr-1" />
                                      {incidente.images.length}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[12px] font-medium text-slate-400">Sem fotos</span>
                              )}
                            </Td>
                            <Td>
                              <div className="inline-flex items-start gap-1.5">
                                <CalendarDays className="h-3.5 w-3.5 shrink-0 mt-0.5 stroke-[1.8] text-slate-400" />
                                <div className="flex flex-col leading-tight">
                                  <span className="font-semibold text-slate-700 tabular-nums text-[12.5px]">
                                    {formatDate(incidente.created_at)}
                                  </span>
                                  <span className="text-[11.5px] text-slate-500 tabular-nums">
                                    {new Date(incidente.created_at).toLocaleTimeString('pt-BR', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </span>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="flex items-center gap-1.5">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => setViewingIncident(incidente)}
                                  className="!h-8 !px-2"
                                  title="Visualizar detalhes"
                                >
                                  <Eye className="h-4 w-4 stroke-[1.9]" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => handleEdit(incidente)}
                                  className="!h-8 !px-2"
                                  title="Editar incidente"
                                >
                                  <Edit3 className="h-4 w-4 stroke-[1.9]" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => confirmAndDelete(incidente.id)}
                                  disabled={deletingId === incidente.id}
                                  className="!h-8 !px-2 hover:!bg-red-50 hover:!text-red-700 hover:!border-red-200"
                                  title="Excluir incidente"
                                >
                                  {deletingId === incidente.id ? (
                                    <Loader2 className="h-4 w-4 stroke-[1.9] animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4 stroke-[1.9]" />
                                  )}
                                </Button>
                              </div>
                            </Td>
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </ModernTable>

                {!loading && pageRange.total > 0 && (
                  <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[13px] font-medium text-slate-500">
                      Mostrando <span className="font-semibold text-slate-700">{pageRange.start}-{pageRange.end}</span> de <span className="font-semibold text-slate-700">{pageRange.total}</span> incidentes
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        aria-label="Primeira página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="Página anterior"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers.map((n, idx) =>
                          n === '...' ? (
                            <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center text-slate-500 text-[13px]">
                              …
                            </span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              aria-label={`Ir para página ${n}`}
                              className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        aria-label="Próxima página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        aria-label="Última página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                )}
              </Fragment>
            )}
          </CardBody>
        </SectionCard>

        {/* ===== MODAL CRIAR / EDITAR ===== */}
        {isModalOpen && (
          <Modal
            title={editingIncidente ? 'Editar Incidente' : 'Novo Incidente'}
            onClose={() => {
              setIsModalOpen(false);
              setEditingIncidente(null);
              setFormData({ vehicle_id: undefined, date: '', time: '', description: '', location: '', involved_employees: user?.employee_id ? [user.employee_id] : [] });
              photoPreviews.forEach(url => URL.revokeObjectURL(url));
              setPhotoFiles([]);
              setPhotoPreviews([]);
            }}
            maxWidthClass="max-w-3xl"
            footer={
              <Fragment>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingIncidente(null);
                    setFormData({ vehicle_id: undefined, date: '', time: '', description: '', location: '', involved_employees: user?.employee_id ? [user.employee_id] : [] });
                    photoPreviews.forEach(url => URL.revokeObjectURL(url));
                    setPhotoFiles([]);
                    setPhotoPreviews([]);
                  }}
                  disabled={submitting}
                  className="h-10 px-5"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  form="form-incidente"
                  variant="primary"
                  disabled={submitting}
                  className="h-10 px-5"
                >
                  {submitting
                    ? (editingIncidente ? 'Atualizando...' : 'Criando...')
                    : (editingIncidente ? 'Atualizar Incidente' : 'Criar Incidente')}
                </Button>
              </Fragment>
            }
          >
            {error && (
              <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} id="form-incidente">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-semibold text-slate-700">
                    Veículo <span className="text-rose-600">*</span>
                  </label>
                  <select
                    name="vehicle_id"
                    value={formData.vehicle_id || ''}
                    onChange={handleInputChange}
                    className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                    required
                  >
                    <option value="" disabled>Selecione o veículo</option>
                    {availableVehicles.map(vehicle => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.brand || '(Sem marca)'} — Placa: {vehicle.license_plate}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-semibold text-slate-700">
                      Data <span className="text-rose-600">*</span>
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date || ''}
                      onChange={handleInputChange}
                      className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-semibold text-slate-700">
                      Hora <span className="text-rose-600">*</span>
                    </label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time || ''}
                      onChange={handleInputChange}
                      className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2 md:col-span-2">
                  <label className="text-[13px] font-semibold text-slate-700">
                    Local <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location || ''}
                    onChange={handleInputChange}
                    placeholder="Endereço ou localização do incidente"
                    className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                    required
                  />
                </div>

                <div className="flex flex-col gap-2 md:col-span-2">
                  <label className="text-[13px] font-semibold text-slate-700">
                    Descrição <span className="text-rose-600">*</span>
                  </label>
                  <textarea
                    name="description"
                    rows={4}
                    value={formData.description || ''}
                    onChange={handleInputChange}
                    placeholder="Descreva detalhes do ocorrido..."
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10 resize-y"
                    required
                  />
                </div>

                <div className="flex flex-col gap-2 md:col-span-2">
                  <label className="text-[13px] font-semibold text-slate-700">
                    Envolvidos
                  </label>
                  <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-[13px] text-sky-900">
                    <div className="flex items-start gap-2">
                      <UserIcon className="h-[18px] w-[18px] shrink-0 mt-[1px] stroke-[1.9] text-sky-700" />
                      <div>
                        Este incidente será registrado automaticamente com o seu vínculo de funcionário.
                      </div>
                    </div>
                    {editingIncidente && (
                      <div className="mt-3 pt-3 border-t border-sky-200/70 space-y-1.5">
                        <span className="text-[11.5px] font-bold uppercase tracking-wider text-sky-700/80">
                          Atualmente vinculados:
                        </span>
                        <div className="mt-1">{renderInvolvedEmployees(editingIncidente.involved_employees)}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-2 md:col-span-2">
                  <label className="text-[13px] font-semibold text-slate-700">
                    Fotos do Incidente
                  </label>
                  <label
                    htmlFor="incidente-upload"
                    className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/60 px-6 py-6 text-center transition-all hover:border-rose-400 hover:bg-rose-50/40"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200">
                      <ImageIcon className="h-[20px] w-[20px] stroke-[1.8] text-rose-600" />
                    </div>
                    <div>
                      <p className="text-[13.5px] font-semibold text-slate-900">Clique para selecionar fotos do incidente</p>
                      <p className="mt-1 text-[12.5px] text-slate-500">PNG, JPG, JPEG — selecione múltiplas</p>
                    </div>
                    <input
                      id="incidente-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                  </label>
                  {photoPreviews.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3 p-3 rounded-xl bg-slate-50 ring-1 ring-slate-200">
                      {photoPreviews.map((src, idx) => (
                        <div key={idx} className="relative group">
                          <img src={src} alt="Preview" className="h-20 w-20 object-cover rounded-lg ring-1 ring-slate-200" />
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(idx)}
                            className="absolute -top-2 -right-2 bg-white text-rose-600 rounded-full w-6 h-6 flex items-center justify-center shadow-md ring-1 ring-slate-200 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-50"
                            title="Remover imagem"
                          >
                            <X className="h-3.5 w-3.5 stroke-[2.2]" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </form>
          </Modal>
        )}

        {/* ===== MODAL VISUALIZAR DETALHES ===== */}
        {viewingIncident && (
          <Modal
            title="Detalhes do Incidente"
            onClose={() => setViewingIncident(null)}
            maxWidthClass="max-w-2xl"
            footer={
              <Button
                type="button"
                variant="secondary"
                onClick={() => setViewingIncident(null)}
                className="h-10 px-5"
              >
                Voltar
              </Button>
            }
          >
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Veículo</span>
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 text-white">
                        <Car className="h-3.5 w-3.5 stroke-[1.9]" />
                      </div>
                      <div>
                        <span className="font-semibold text-slate-900 block">{resolveVehicle(viewingIncident).brand}</span>
                        <span className="text-xs text-slate-500 font-mono">{resolveVehicle(viewingIncident).license_plate || '-'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Envolvidos</span>
                    <div>{renderInvolvedEmployees(viewingIncident.involved_employees)}</div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Data</span>
                    <div className="inline-flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                      <span className="font-semibold text-slate-700 tabular-nums">{formatDate(viewingIncident.date)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Hora</span>
                    <div className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                      <span className="font-semibold text-slate-700 tabular-nums">{viewingIncident.time || '-'}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 md:col-span-2">
                    <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Local</span>
                    <div className="inline-flex items-start gap-1.5">
                      <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5 stroke-[1.8] text-slate-400" />
                      <span className="font-semibold text-slate-700">{viewingIncident.location || '-'}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 md:col-span-2">
                    <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Descrição</span>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[13.5px] leading-relaxed text-slate-800 whitespace-pre-wrap">
                      {viewingIncident.description || '-'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 md:col-span-2">
                    <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Registrado em</span>
                    <span className="font-semibold text-slate-700 tabular-nums">
                      {formatDateTime(viewingIncident.created_at)}
                    </span>
                  </div>
                  {viewingIncident.images && viewingIncident.images.length > 0 && (
                    <div className="flex flex-col gap-1.5 md:col-span-2">
                      <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">
                        Fotos do Incidente ({viewingIncident.images.length})
                      </span>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 p-3 rounded-xl bg-slate-50 ring-1 ring-slate-200">
                        {viewingIncident.images.map((img: { url: string }, idx: number) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setSelectedImage(img.url)}
                            className="relative aspect-[4/3] overflow-hidden rounded-lg ring-1 ring-slate-200 hover:ring-rose-300 transition-all hover:scale-[1.03] hover:shadow-md group"
                            title={`Abrir foto ${idx + 1}`}
                          >
                            <img src={img.url} alt={`Incidente foto ${idx + 1}`} className="h-full w-full object-cover" />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                              <Eye className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md stroke-[2]" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
            </div>
          </Modal>
        )}

        {/* ===== LIGHTBOX FOTO ===== */}
        {selectedImage && (
          <div
            className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-3 sm:p-6 backdrop-blur-sm animate-in fade-in"
            onClick={() => setSelectedImage(null)}
          >
            <div
              className="relative max-w-5xl max-h-[92vh] w-full"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-3 -right-3 sm:top-4 sm:right-4 z-10 text-white bg-black/60 hover:bg-black/80 rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-colors"
                aria-label="Fechar"
              >
                <X className="h-5 w-5 stroke-[2]" />
              </button>
              <img
                src={selectedImage}
                alt="Foto ampliada"
                className="w-full h-auto max-h-[92vh] object-contain rounded-xl shadow-2xl"
              />
            </div>
          </div>
        )}

        <ConfirmModal
          isOpen={deletingId !== null}
          title="Excluir este incidente?"
          description="Esta ação não pode ser desfeita. Os dados, fotos em anexo e vínculos com viagens serão permanentemente removidos."
          confirmVariant="danger"
          confirmLabel="Sim, excluir incidente"
          cancelLabel="Manter incidente"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeletingId(null)}
        />
      </div>
    </div>
  );
};

export default IncidentesPage;
